// frontend/src/features/sales-rep/components/RepActivityPieChart.tsx
import React, { useState, useMemo } from 'react';
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, Label
} from 'recharts';
import { RepActivityData } from '../services/salesRepAPI';

interface RepActivityPieChartProps {
    data: RepActivityData;
}

const COLORS = ['#2e7d32', '#c62828'];
const ITEMS_PER_PAGE = 15;

type DetailView = 'comVenda' | 'semVenda' | null;

const formatCurrency = (value: number) =>
    Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact', compactDisplay: 'short' } as any);

// ==================== MODAL ====================
const RepDetailModal: React.FC<{
    type: DetailView;
    data: RepActivityData;
    onClose: () => void;
}> = ({ type, data, onClose }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');

    const isComVenda = type === 'comVenda';
    const rawList: any[] = isComVenda ? data.repsComVenda : data.repsSemVenda;
    const color = isComVenda ? '#2e7d32' : '#c62828';
    const title = isComVenda ? 'Representantes Com Venda' : 'Representantes Sem Venda';

    const filteredList = useMemo(() => {
        if (!searchTerm.trim()) return rawList;
        const term = searchTerm.toLowerCase();
        return rawList.filter((rep: any) => rep.nome?.toLowerCase().includes(term));
    }, [rawList, searchTerm]);

    const totalPages = Math.ceil(filteredList.length / ITEMS_PER_PAGE);
    const paginatedList = filteredList.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page);
    };

    // Reset page when search changes
    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                animation: 'fadeIn 0.2s ease'
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: 'var(--bg-widget)',
                    borderRadius: 'var(--radius-base, 8px)',
                    width: '90%',
                    maxWidth: '600px',
                    maxHeight: '80vh',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px 24px 16px',
                    borderBottom: '1px solid var(--border-primary)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                            width: '14px',
                            height: '14px',
                            borderRadius: 'var(--radius-base, 8px)',
                            backgroundColor: color
                        }} />
                        <h3 style={{ margin: 0, fontSize: '17px', color: '#1B2A49', fontWeight: '600' }}>
                            {title}
                        </h3>
                        <span style={{
                            backgroundColor: color + '15',
                            color: color,
                            padding: '2px 10px',
                            borderRadius: 'var(--radius-base, 8px)',
                            fontSize: '12px',
                            fontWeight: '600'
                        }}>
                            {filteredList.length}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '22px',
                            cursor: 'pointer',
                            color: '#999',
                            padding: '0 4px',
                            lineHeight: 1
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Search */}
                <div style={{ padding: '12px 24px' }}>
                    <input
                        type="text"
                        placeholder="Buscar representante..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '10px 14px',
                            border: '1px solid var(--border-primary)',
                            borderRadius: 'var(--radius-base, 8px)',
                            fontSize: '13px',
                            outline: 'none',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => { e.target.style.borderColor = color; }}
                        onBlur={(e) => { e.target.style.borderColor = '#e0e0e0'; }}
                    />
                </div>

                {/* Table Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 24px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#888',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    <span style={{ width: '40px' }}>#</span>
                    <span style={{ flex: 1 }}>Representante</span>
                    {isComVenda && <span style={{ width: '80px', textAlign: 'right' }}>Pares</span>}
                    {isComVenda && <span style={{ width: '120px', textAlign: 'right' }}>Faturamento</span>}
                </div>

                {/* List */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
                    {paginatedList.length === 0 ? (
                        <div style={{ textAlign: 'center', color: '#999', padding: '40px 0', fontSize: '14px' }}>
                            {searchTerm ? 'Nenhum representante encontrado.' : 'Nenhum representante nesta categoria.'}
                        </div>
                    ) : (
                        paginatedList.map((rep: any, index: number) => {
                            const globalIndex = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
                            return (
                                <div
                                    key={rep.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '10px 0',
                                        borderBottom: '1px solid var(--border-primary)',
                                        transition: 'background-color 0.15s'
                                    }}
                                >
                                    <span style={{
                                        width: '40px',
                                        fontSize: '12px',
                                        color: '#aaa',
                                        fontWeight: '500'
                                    }}>
                                        {globalIndex}
                                    </span>
                                    <span style={{
                                        flex: 1,
                                        fontSize: '13px',
                                        color: '#333',
                                        fontWeight: '500'
                                    }}>
                                        {rep.nome}
                                    </span>
                                    {isComVenda && rep.totalPares !== undefined && (
                                        <span style={{
                                            width: '80px',
                                            textAlign: 'right',
                                            fontSize: '13px',
                                            color: '#1B2A49',
                                            fontWeight: '500'
                                        }}>
                                            {Number(rep.totalPares).toLocaleString('pt-BR')}
                                        </span>
                                    )}
                                    {isComVenda && rep.totalVenda !== undefined && (
                                        <span style={{
                                            width: '120px',
                                            textAlign: 'right',
                                            fontSize: '13px',
                                            color: '#2e7d32',
                                            fontWeight: '600'
                                        }}>
                                            {formatCurrency(rep.totalVenda)}
                                        </span>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '14px 24px',
                        borderTop: '1px solid #eee',
                        backgroundColor: '#fafafa'
                    }}>
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            style={{
                                border: '1px solid var(--border-primary)',
                                borderRadius: 'var(--radius-base, 8px)',
                                padding: '6px 12px',
                                fontSize: '12px',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                backgroundColor: 'var(--bg-widget)',
                                color: currentPage === 1 ? '#ccc' : '#666',
                                transition: 'all 0.15s'
                            }}
                        >
                            ← Anterior
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(page => {
                                if (totalPages <= 5) return true;
                                if (page === 1 || page === totalPages) return true;
                                return Math.abs(page - currentPage) <= 1;
                            })
                            .reduce((acc: (number | string)[], page, i, arr) => {
                                if (i > 0 && typeof arr[i - 1] === 'number' && (page as number) - (arr[i - 1] as number) > 1) {
                                    acc.push('...');
                                }
                                acc.push(page);
                                return acc;
                            }, [])
                            .map((item, i) => (
                                item === '...' ? (
                                    <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: '#999', fontSize: '12px' }}>...</span>
                                ) : (
                                    <button
                                        key={item}
                                        onClick={() => handlePageChange(item as number)}
                                        style={{
                                            border: currentPage === item ? `1px solid ${color}` : '1px solid #ddd',
                                            borderRadius: 'var(--radius-base, 8px)',
                                            padding: '6px 10px',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            backgroundColor: currentPage === item ? color : '#fff',
                                            color: currentPage === item ? '#fff' : '#666',
                                            fontWeight: currentPage === item ? '600' : '400',
                                            transition: 'all 0.15s',
                                            minWidth: '32px'
                                        }}
                                    >
                                        {item}
                                    </button>
                                )
                            ))
                        }

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            style={{
                                border: '1px solid var(--border-primary)',
                                borderRadius: 'var(--radius-base, 8px)',
                                padding: '6px 12px',
                                fontSize: '12px',
                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                backgroundColor: 'var(--bg-widget)',
                                color: currentPage === totalPages ? '#ccc' : '#666',
                                transition: 'all 0.15s'
                            }}
                        >
                            Próxima →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ==================== TOOLTIP ====================
const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        const { name, value, payload: itemPayload } = payload[0];
        return (
            <div style={{
                backgroundColor: 'var(--bg-widget)',
                padding: '10px 14px',
                border: '1px solid var(--border-primary)',
                borderRadius: 'var(--radius-base, 8px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                fontSize: '13px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: itemPayload.fill }} />
                    <strong>{name}</strong>
                </div>
                <div style={{ color: '#555' }}>
                    {value} representante{value !== 1 ? 's' : ''} ({itemPayload.percent}%)
                </div>
                <div style={{ color: '#999', fontSize: '11px', marginTop: '4px', fontStyle: 'italic' }}>
                    Clique para ver detalhes
                </div>
            </div>
        );
    }
    return null;
};

const CustomLegend = ({ payload }: any) => (
    <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', paddingTop: '8px', fontSize: '13px' }}>
        {payload?.map((entry: any, index: number) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: entry.color }} />
                <span style={{ color: '#333' }}>{entry.value}</span>
            </div>
        ))}
    </div>
);

// ==================== MAIN COMPONENT ====================
const RepActivityPieChart: React.FC<RepActivityPieChartProps> = ({ data }) => {
    const [detailView, setDetailView] = useState<DetailView>(null);
    const total = data.comVenda + data.semVenda;

    if (total === 0) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                Nenhum representante cadastrado.
            </div>
        );
    }

    const comVendaPercent = ((data.comVenda / total) * 100).toFixed(1);
    const semVendaPercent = ((data.semVenda / total) * 100).toFixed(1);

    const chartData = [
        { name: 'Com Venda', value: data.comVenda, percent: comVendaPercent, key: 'comVenda' as const },
        { name: 'Sem Venda', value: data.semVenda, percent: semVendaPercent, key: 'semVenda' as const },
    ];

    const handlePieClick = (_: any, index: number) => {
        setDetailView(chartData[index].key);
    };

    return (
        <>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={chartData}
                        cx="50%"
                        cy="45%"
                        innerRadius={65}
                        outerRadius={100}
                        dataKey="value"
                        nameKey="name"
                        paddingAngle={3}
                        strokeWidth={2}
                        stroke="#fff"
                        onClick={handlePieClick}
                        style={{ cursor: 'pointer' }}
                    >
                        {chartData.map((_entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index]} />
                        ))}
                        <Label value={`${comVendaPercent}%`} position="center" dy={-8} fill="#2e7d32" fontSize="22" fontWeight="bold" />
                        <Label value="ativos" position="center" dy={12} fill="#666" fontSize="13" />
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend content={<CustomLegend />} />
                </PieChart>
            </ResponsiveContainer>

            {detailView && (
                <RepDetailModal
                    type={detailView}
                    data={data}
                    onClose={() => setDetailView(null)}
                />
            )}
        </>
    );
};

export default RepActivityPieChart;
